import {styled} from '@mui/material/styles';
import {forwardRef} from 'react';
import type {ListProps} from '../List/List';
import {List} from '../List/List';
import {NavItem} from './NavItem';
import {NavItemButton} from './NavItemButton';
import {NavItemCollapse} from './NavItemCollapse';
import {NavItemDropdown} from './NavItemDropdown';
import {NavigationLinkOptions} from './types';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface NavProps extends ListProps {
    // headerBreakpointActive?: NavProps['headerBreakpointActive'];
    variant?: 'top' | 'fixedSide' | 'side';
    /**
     * Boolean to restrict wrap header and navigation content in a Container component. Use for contained layout.
     * @default false
     */
    isContained?: boolean;
    items?: NavigationLinkOptions[];
}

const StyledNav = styled(List, {
    ...excludeComponentProps('variant', 'toggleSubNavigation', 'isPanelOpen', 'handleCloseHeaderMobileMenu', 'headerMobileMenuPosition', 'isHeaderNavCollapsed', 'headerMobileMenuOpen', 'headerBreakpointActive', 'headerMenuBreakpoint', 'isContained')
})<NavProps>(({
    theme, variant, isContained
}) => ({
    display: 'flex',
    padding: `0 ${isContained ? 0 : theme.tokens.FuelSpacing2}`,
    backgroundColor: theme.tokens.FuelColorWhite,

    ...variant === 'top' && {
        borderBottom: `solid 1px ${theme.tokens.FuelColorBorderLight}`,
        gridArea: 'top-nav'
    },

    ...variant === 'fixedSide' && {
        display: 'block',
        minWidth: '15rem',
        boxShadow: 'none',
        padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing1}`,
        height: 'calc(100vh - 48px)',
        paddingLeft: 0,
        paddingRight: 0,
        borderRight: `solid 1px ${theme.tokens.FuelColorBorderBase}`,
        gridArea: 'fixed-side-nav'
    },

    ...variant === 'side' && {
        display: 'block',
        paddingTop: `${theme.tokens.FuelSpacing2}`,
        paddingBottom: `${theme.tokens.FuelSpacing2}`,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: theme.tokens.FuelShadowMd,
        borderRadius: theme.tokens.FuelBorderRadiusLg,
        gridArea: 'side-nav',

        '& > .MuiList-root': {
            width: '100%',
            margin: '.25rem 0 .5rem 0',
            flexDirection: 'column'
        }
    }
}));

const StyledList = styled(List, {
    ...excludeComponentProps('headerBreakpointActive', 'variant', 'isContained')
})<NavProps>(({
    theme, variant, isContained
}) => ({
    display: 'inline-flex',
    paddingTop: 0,
    paddingBottom: 0,

    ...variant === 'top' && isContained && {
        justifyContent: 'start',
        width: '100%',
        maxWidth: theme.breakpoints.values.lg,
        margin: '0 auto',
        padding: `0 ${theme.tokens.FuelSpacing2}`,

        '& .MuiListItem-root': {
            width: 'auto'
        }
    },

    ...variant === 'side' && {
        display: 'block',
        paddingLeft: theme.tokens.FuelSpacing1,
        paddingRight: theme.tokens.FuelSpacing1,

        '& .MuiListItem-root:not(:first-of-type)': {
            marginTop: theme.tokens.FuelSpacing1
        }
    },

    ...variant === 'fixedSide' && {
        display: 'block',
        width: '100%',
        paddingLeft: theme.tokens.FuelSpacing1,
        paddingRight: theme.tokens.FuelSpacing1,

        '& .MuiListItem-root:not(:first-of-type)': {
            marginTop: theme.tokens.FuelSpacing1
        }
    }
}));

export const Nav = forwardRef<HTMLUListElement, NavProps>(({
    children, variant, items, isContained, ...props
}, ref) => {
    const isTop = variant === 'top' || variant === undefined;

    // auto-generate links if user inputs 'items'
    if (Array.isArray(items) && items.length > 0) {
        return (
            <StyledNav
                {...props}
                component='nav'
                isContained={isContained}
                ref={ref}
                variant={variant}
            >
                <StyledList
                    isContained={isContained}
                    variant={variant || 'top'}
                >
                    {items.map((item, key) => {
                        return Array.isArray(item.subNavigationItems) && item.subNavigationItems.length > 0 ? (
                            <NavItem key={`nav-mobile-${key}`}>
                                {isTop ? (
                                    <NavItemDropdown
                                        {...item}
                                        items={item.subNavigationItems}
                                    >
                                        {item.label}
                                    </NavItemDropdown>
                                ) : (
                                    <NavItemCollapse
                                        {...item}
                                        items={item.subNavigationItems}
                                    >
                                        {item.label}
                                    </NavItemCollapse>
                                )}
                            </NavItem>
                        ) : (
                            <NavItem key={`nav-mobile-${key}`}>
                                <NavItemButton
                                    {...item}
                                    orientation={isTop ? 'top' : 'side'}
                                >
                                    {item.label}
                                </NavItemButton>
                            </NavItem>
                        );
                    })}
                </StyledList>
            </StyledNav>
        );
    }

    return (
        <StyledNav
            {...props}
            component='nav'
            ref={ref}
            variant={variant}
        >
            <StyledList
                isContained={isContained}
                variant={variant}
            >
                {children}
            </StyledList>
        </StyledNav>
    );
});

Nav.displayName = 'Nav';
